import React, { useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ExclamationTriangleIcon, ClipboardDocumentIcon, CheckIcon, GlobeAltIcon } from '@heroicons/react/24/outline';
import LanguageSelector from '../LanguageSelector';
import { trackSignupClick } from '../../services/analyticsTrackingService';
import { getCurrentPageUrl, openInExternalBrowser } from '../../utils/browserDetection';
import { SAILLAB_LOGO_URL, getSystemImageUrl } from '../../utils/media';

interface AuthLayoutProps {
  /**
   * The main content to render in the left column (forms, buttons, etc.)
   */
  children: React.ReactNode;
  /**
   * Title displayed at the top of the content area
   */
  title: string;
  /**
   * Optional subtitle displayed below the title
   */
  subtitle?: string;
  /**
   * Type of auth page - determines the account toggle link
   */
  mode: 'login' | 'signup';
  /**
   * Optional error message to display
   */
  error?: string | null;
  /**
   * Whether the user is in an in-app browser (WebView) that blocks Google OAuth
   */
  isInAppBrowser?: boolean;
  /**
   * Override the account-toggle link paths. Defaults to /login and /signup,
   * but the affiliate portal passes /affiliates/login and /affiliates/signup
   * so the same layout can host both flows.
   */
  accountTogglePaths?: { login: string; signup: string };
  /**
   * Override the toggle copy when the audience differs from the main app
   * (e.g., affiliate signup vs SailLab user signup).
   */
  accountToggleCopy?: {
    noAccount?: string;
    signUp?: string;
    hasAccount?: string;
    logIn?: string;
  };
  /**
   * Override the decorative right-column image. Defaults to the mode-based
   * sailboat images. The affiliate portal passes its own branded image so the
   * shared layout can host both the main app and affiliate flows.
   */
  image?: { src: string; alt: string };
}

/**
 * Image configuration for each auth mode
 * - Login: Sailboat with cats image
 * - Signup: Sailboat with tree image
 */
const authImages = {
  login: {
    src: getSystemImageUrl('Sailboatwithcats.jpg'),
    alt: 'Sailboat with cats',
  },
  signup: {
    src: getSystemImageUrl('Sailboatwithtree.png'),
    alt: 'Sailboat with tree',
  },
};

/**
 * AuthLayout - Shared two-column layout for Login and Signup pages
 *
 * Matches the OnboardingWizard design:
 * - Left column (60%): Logo, title, content, terms, account toggle, language selector
 * - Right column (40%): Decorative sailboat image (hidden on mobile)
 */
const AuthLayout: React.FC<AuthLayoutProps> = ({
  children,
  title,
  subtitle,
  mode,
  error,
  isInAppBrowser = false,
  accountTogglePaths,
  accountToggleCopy,
  image,
}) => {
  const { t } = useTranslation();
  const currentImage = image ?? authImages[mode];
  const [copied, setCopied] = useState(false);
  const [searchParams] = useSearchParams();

  // Preserve ?lang= when switching between Login and Signup pages.
  // Only add the param if the current URL explicitly has it (e.g., from CTA link).
  // Without ?lang= in URL, language still persists via localStorage (set by i18n pre-sync).
  const urlLang = searchParams.get('lang');
  const baseSignup = accountTogglePaths?.signup || '/signup';
  const baseLogin = accountTogglePaths?.login || '/login';
  const signupPath = urlLang ? `${baseSignup}${baseSignup.includes('?') ? '&' : '?'}lang=${urlLang}` : baseSignup;
  const loginPath = urlLang ? `${baseLogin}${baseLogin.includes('?') ? '&' : '?'}lang=${urlLang}` : baseLogin;

  const handleOpenInBrowser = useCallback(() => {
    const url = getCurrentPageUrl();
    openInExternalBrowser(url);
  }, []);

  const handleCopyUrl = useCallback(async () => {
    try {
      const url = getCurrentPageUrl();
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API not available in some WebViews — select text as fallback
    }
  }, []);

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      {/* Left Column - Auth Content */}
      <div className="flex-1 flex flex-col h-screen lg:max-w-[60%]">
        {/* Header - Logo - Fixed at top */}
        <div className="px-6 lg:px-8 pt-6 lg:pt-8 pb-4 flex-shrink-0">
          <div className="w-full max-w-md mx-auto">
            {/* Logo - Using official SailLab logo */}
            <img
              src={SAILLAB_LOGO_URL}
              alt="SailLab"
              className="h-10 w-auto"
            />
          </div>
        </div>

        {/* Main Content Area - Centered vertically */}
        <div className="flex-1 flex flex-col items-center justify-center px-6 lg:px-8 py-8 overflow-y-auto min-h-0">
          <div className="w-full max-w-md">
            {isInAppBrowser ? (
              /* ============================================
               * In-App Browser Warning
               * Shown when user opens from LINE, Instagram, etc.
               * Google OAuth blocks these browsers (403: disallowed_useragent)
               * ============================================ */
              <>
                {/* Warning Icon */}
                <div className="flex justify-center mb-6">
                  <div className="p-3 bg-yellow-50 rounded-full">
                    <ExclamationTriangleIcon className="h-10 w-10 text-yellow-600" />
                  </div>
                </div>

                {/* Title and Explanation */}
                <div className="mb-6 text-center">
                  <h1 className="text-2xl font-bold text-gray-900 mb-3">
                    {t('common:auth.inAppBrowser.title', 'Open in your browser')}
                  </h1>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {t(
                      'common:auth.inAppBrowser.description',
                      'For security reasons, Google sign-in is not available in this browser. Please open this page in your default browser to continue.'
                    )}
                  </p>
                </div>

                {/* Open in Browser Button */}
                <div className="space-y-4">
                  <button
                    type="button"
                    onClick={handleOpenInBrowser}
                    className="btn-primary btn-lg w-full flex items-center justify-center gap-2"
                  >
                    <GlobeAltIcon className="h-5 w-5" />
                    {t('common:auth.inAppBrowser.openInBrowser', 'Open in Browser')}
                  </button>
                </div>

                {/* Copy URL Fallback */}
                <div className="mt-6">
                  <p className="text-xs text-gray-500 text-center mb-2">
                    {t('common:auth.inAppBrowser.orCopyLink', 'Or copy this link and paste it in your browser:')}
                  </p>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-700 truncate select-all">
                      {getCurrentPageUrl()}
                    </div>
                    <button
                      type="button"
                      onClick={handleCopyUrl}
                      className="btn-secondary btn-sm flex items-center gap-1 flex-shrink-0"
                      aria-label={t('common:actions.copy', 'Copy')}
                    >
                      {copied ? (
                        <CheckIcon className="h-4 w-4 text-green-600" />
                      ) : (
                        <ClipboardDocumentIcon className="h-4 w-4" />
                      )}
                      {copied
                        ? t('common:auth.inAppBrowser.copied', 'Copied!')
                        : t('common:actions.copy', 'Copy')}
                    </button>
                  </div>
                </div>

                {/* Helpful Tip */}
                <div className="mt-6 p-3 bg-primary-50 rounded-lg">
                  <p className="text-xs text-primary-700 text-center leading-relaxed">
                    <span className="font-medium">
                      {t('common:auth.inAppBrowser.tipLabel', 'Tip:')}
                    </span>{' '}
                    {t(
                      'common:auth.inAppBrowser.tipText',
                      'You can also tap the "⋮" or "..." menu at the top of this screen and select "Open in browser".'
                    )}
                  </p>
                </div>
              </>
            ) : (
              /* ============================================
               * Normal Auth Content (OAuth buttons, etc.)
               * ============================================ */
              <>
                {/* Title and Subtitle */}
                <div className="mb-8">
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">
                    {title}
                  </h1>
                  {subtitle && (
                    <p className="text-gray-600">
                      {subtitle}
                    </p>
                  )}
                </div>

                {/* Error Message */}
                {error && (
                  <div className="mb-6 bg-red-50 border-l-4 border-red-500 p-4 rounded-r">
                    <p className="text-red-700 text-sm">{error}</p>
                  </div>
                )}

                {/* Main Content (OAuth buttons, etc.) */}
                <div className="space-y-4">
                  {children}
                </div>

                {/* Terms and Privacy */}
                <div className="mt-8 text-center text-sm text-gray-500">
                  <p>
                    {t('common:auth.termsAgreement', 'By proceeding, you agree to the')}{' '}
                    <a
                      href="https://saillab.co.jp/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:text-primary-700 underline"
                    >
                      {t('common:auth.termsOfService', 'Terms of Service')}
                    </a>
                    {' '}{t('common:auth.and', 'and')}{' '}
                    <a
                      href="https://saillab.co.jp/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:text-primary-700 underline"
                    >
                      {t('common:auth.privacyPolicy', 'Privacy Policy')}
                    </a>
                  </p>
                </div>

                {/* Account Toggle */}
                <div className="mt-6 text-center text-sm">
                  {mode === 'login' ? (
                    <p className="text-gray-600">
                      {accountToggleCopy?.noAccount ||
                        t('common:auth.noAccount', "Don't have an account?")}{' '}
                      <Link
                        to={signupPath}
                        onClick={() => trackSignupClick({ source: 'login_page' })}
                        className="font-medium text-primary hover:text-primary-700"
                      >
                        {accountToggleCopy?.signUp || t('common:auth.signUp', 'Sign up')}
                      </Link>
                    </p>
                  ) : (
                    <p className="text-gray-600">
                      {accountToggleCopy?.hasAccount ||
                        t('common:auth.hasAccount', 'Already have an account?')}{' '}
                      <Link
                        to={loginPath}
                        className="font-medium text-primary hover:text-primary-700"
                      >
                        {accountToggleCopy?.logIn || t('common:auth.logIn', 'Log in')}
                      </Link>
                    </p>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Footer - Language Selector - Fixed at bottom */}
        <div className="px-6 lg:px-8 pb-6 lg:pb-8 flex-shrink-0">
          <div className="w-full max-w-md mx-auto">
            <LanguageSelector />
          </div>
        </div>
      </div>

      {/* Right Column - Sailboat Background Image (Hidden on mobile) */}
      <div className="hidden lg:block lg:w-[40%] h-screen relative overflow-hidden flex-shrink-0">
        <img
          src={currentImage.src}
          alt={currentImage.alt}
          className="absolute inset-0 w-full h-full object-cover"
          style={{
            objectPosition: 'center center',
          }}
        />
      </div>
    </div>
  );
};

export default AuthLayout;
